import React from 'react';
import { ChevronUp, ChevronDown, MoveUp, MoveDown, ArrowUpDown, ArrowUp, ArrowDown, X } from 'lucide-react';
import { Button } from '../ui/button';

// Sort icon helper - renders the appropriate sort icon based on column state
const renderSortIcon = (sortColumn, sortDirection, column) => {
  if (sortColumn !== column) {
    return <ArrowUpDown className="w-3 h-3 text-slate-400" />;
  }
  return sortDirection === 'asc' 
    ? <ArrowUp className="w-3 h-3 text-blue-600" />
    : <ArrowDown className="w-3 h-3 text-blue-600" />;
};

// Panel move buttons - standalone component
const PanelMoveButtons = ({ panelId, movePanelUp, movePanelDown, isFirst, isLast, lightTheme = false }) => (
  <div className="flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
    <Button
      variant="ghost"
      size="sm"
      onClick={() => movePanelUp(panelId)}
      disabled={isFirst}
      className={`p-1 h-auto ${isFirst ? 'opacity-30 cursor-not-allowed' : lightTheme ? 'text-white/60 hover:text-white hover:bg-white/20' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-200'}`}
      title="Move panel up"
    >
      <MoveUp className="w-4 h-4" />
    </Button>
    <Button
      variant="ghost"
      size="sm"
      onClick={() => movePanelDown(panelId)}
      disabled={isLast}
      className={`p-1 h-auto ${isLast ? 'opacity-30 cursor-not-allowed' : lightTheme ? 'text-white/60 hover:text-white hover:bg-white/20' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-200'}`}
      title="Move panel down"
    >
      <MoveDown className="w-4 h-4" />
    </Button>
  </div>
);

const StockAnalyticsPanel = ({
  stocks,
  showStockAnalytics,
  toggleStockAnalytics,
  mobilePreview,
  summary,
  selectedPlatform,
  selectedStockPopup,
  setSelectedStockPopup,
  formatCurrencyPrivate,
  privacyMode,
  movePanelUp,
  movePanelDown,
  isFirst,
  isLast,
  analyticsSortColumn,
  analyticsSortDirection,
  handleAnalyticsSort,
  getAnalyticsSortedStocks,
}) => {
  return (
    <div className={`${mobilePreview ? 'mb-2' : 'mb-2 sm:mb-4'} scroll-mt-32 sm:scroll-mt-0`}>
      <div className="bg-white rounded-xl shadow-md border-2 border-slate-200 overflow-hidden">
        {/* Mobile Header - Clickable to expand/collapse */}
        <div 
          className="sm:hidden bg-gradient-to-r from-blue-600 to-indigo-600 px-3 py-3 cursor-pointer"
          onClick={toggleStockAnalytics}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                <ArrowUpDown className="w-4 h-4 text-white" />
              </div>
              <div>
                <h2 className="text-base font-bold text-white">Stock Analytics</h2>
                <p className="text-[10px] text-white/70">Price & Performance</p>
              </div>
            </div>
            <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-lg text-white">
              {showStockAnalytics ? (
                <ChevronUp className="w-5 h-5" />
              ) : (
                <ChevronDown className="w-5 h-5" />
              )}
            </div>
          </div>
        </div>
        
        {/* Desktop Header */}
        <div 
          className="hidden sm:block bg-gradient-to-r from-blue-600 to-indigo-600 px-4 sm:px-5 py-3 cursor-pointer"
          onClick={toggleStockAnalytics}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <PanelMoveButtons panelId="analytics" movePanelUp={movePanelUp} movePanelDown={movePanelDown} isFirst={isFirst} isLast={isLast} lightTheme={true} />
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm">
                <ArrowUpDown className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg sm:text-xl font-bold text-white">Stock Analytics</h2>
                <p className="text-xs text-white/70">Price & Performance View</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => { e.stopPropagation(); toggleStockAnalytics(); }}
              className="text-white/80 hover:text-white hover:bg-white/20 p-1 h-auto"
            >
              {showStockAnalytics ? (
                <ChevronUp className="w-5 h-5" />
              ) : (
                <ChevronDown className="w-5 h-5" />
              )}
            </Button>
          </div>
        </div>

        {/* Analytics Content */}
        {showStockAnalytics && stocks.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gradient-to-r from-slate-200 to-slate-100 sm:from-slate-200 sm:to-slate-100 text-xs font-bold text-slate-700 uppercase tracking-wide border-b-2 border-slate-300">
                  <th className="text-left py-3 px-2 sm:px-4">
                    <button onClick={() => handleAnalyticsSort('symbol')} className="flex items-center gap-1 hover:text-slate-900">
                      Symbol {renderSortIcon(analyticsSortColumn, analyticsSortDirection, 'symbol')}
                    </button>
                  </th>
                  <th className="text-left py-3 px-2 sm:px-4">
                    <button onClick={() => handleAnalyticsSort('name')} className="flex items-center gap-1 hover:text-slate-900">
                      Name {renderSortIcon(analyticsSortColumn, analyticsSortDirection, 'name')}
                    </button>
                  </th>
                  <th className="text-right py-3 px-2 sm:px-4">
                    <button onClick={() => handleAnalyticsSort('current')} className="flex items-center gap-1 justify-end hover:text-slate-900">
                      <span className="hidden sm:inline">Current</span>
                      <span className="sm:hidden">Price</span>
                      {renderSortIcon(analyticsSortColumn, analyticsSortDirection, 'current')}
                    </button>
                  </th>
                  <th className="text-right py-3 px-4 hidden sm:table-cell">
                    <button onClick={() => handleAnalyticsSort('costBasis')} className="flex items-center gap-1 justify-end hover:text-slate-900">
                      Cost Basis {renderSortIcon(analyticsSortColumn, analyticsSortDirection, 'costBasis')}
                    </button>
                  </th>
                  <th className="text-right py-3 px-4 hidden sm:table-cell">
                    <button onClick={() => handleAnalyticsSort('week52High')} className="flex items-center gap-1 justify-end hover:text-slate-900">
                      52W High {renderSortIcon(analyticsSortColumn, analyticsSortDirection, 'week52High')}
                    </button>
                  </th>
                  <th className="text-center py-3 px-4 hidden sm:table-cell">
                    <button onClick={() => handleAnalyticsSort('percentFromHigh')} className="flex items-center gap-1 justify-center hover:text-slate-900">
                      % From High {renderSortIcon(analyticsSortColumn, analyticsSortDirection, 'percentFromHigh')}
                    </button>
                  </th>
                  <th className="text-center py-3 px-4 hidden sm:table-cell">
                    <button onClick={() => handleAnalyticsSort('percentGain')} className="flex items-center gap-1 justify-center hover:text-slate-900">
                      % Gain {renderSortIcon(analyticsSortColumn, analyticsSortDirection, 'percentGain')}
                    </button>
                  </th>
                  <th className="text-center py-3 px-4 hidden sm:table-cell">
                    <button onClick={() => handleAnalyticsSort('weight')} className="flex items-center gap-1 justify-center hover:text-slate-900">
                      Value Weight {renderSortIcon(analyticsSortColumn, analyticsSortDirection, 'weight')}
                    </button>
                  </th>
                  <th className="text-right py-3 px-4 hidden sm:table-cell">
                    <button onClick={() => handleAnalyticsSort('volume')} className="flex items-center gap-1 justify-end hover:text-slate-900">
                      Volume {renderSortIcon(analyticsSortColumn, analyticsSortDirection, 'volume')}
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {getAnalyticsSortedStocks().map((stock, index) => {
                  const totalValue = stock.currentPrice * stock.shares;
                  const costTotal = stock.costBasis * stock.shares;
                  const gainLoss = totalValue - costTotal;
                  const gainLossPercent = costTotal > 0 ? ((gainLoss / costTotal) * 100).toFixed(2) : 0;
                  const week52High = stock.week52High || stock.currentPrice;
                  const percentFromHigh = week52High > 0 ? (((stock.currentPrice - week52High) / week52High) * 100).toFixed(1) : 0;
                  const portfolioWeight = summary.totalValue > 0 ? ((totalValue / summary.totalValue) * 100).toFixed(1) : 0;
                  const isNearHigh = parseFloat(percentFromHigh) >= -5;
                  const isFarFromHigh = parseFloat(percentFromHigh) <= -20;
                  
                  return (
                    <tr key={stock.id} className={`border-t border-slate-100 hover:bg-slate-50 ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}>
                      <td className="py-3 sm:py-2 px-2 sm:px-4">
                        <a 
                          href={`https://www.google.com/finance/quote/${stock.symbol}:${stock.symbol.includes('BINANCE:') ? 'CRYPTO' : 'NASDAQ'}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-bold text-blue-600 hover:text-blue-800 hover:underline text-sm sm:text-sm"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {stock.symbol}
                        </a>
                      </td>
                      <td className="py-3 sm:py-2 px-2 sm:px-4">
                        {/* Mobile: Clickable name that shows popup */}
                        <button
                          className="sm:hidden text-left text-slate-700 text-sm truncate max-w-[120px] block underline decoration-dotted underline-offset-2 hover:text-blue-600"
                          onClick={() => setSelectedStockPopup({
                            ...stock,
                            totalValue,
                            costTotal,
                            gainLoss,
                            gainLossPercent,
                            week52High,
                            percentFromHigh,
                            portfolioWeight
                          })}
                        >
                          {stock.name || stock.symbol}
                        </button>
                        {/* Desktop: Normal text */}
                        <span className="hidden sm:block text-slate-600 text-xs truncate max-w-[150px]">{stock.name || stock.symbol}</span>
                      </td>
                      <td className="py-3 sm:py-2 px-2 sm:px-4 text-right">
                        <span className="font-semibold text-slate-900 text-sm sm:text-sm">${privacyMode ? '•••' : stock.currentPrice?.toFixed(2)}</span>
                      </td>
                      <td className="py-2 px-4 text-right hidden sm:table-cell">
                        <span className="text-slate-600 text-sm">${privacyMode ? '•••' : stock.costBasis?.toFixed(2)}</span>
                      </td>
                      <td className="py-2 px-4 text-right hidden sm:table-cell">
                        <span className="text-slate-600 text-sm">${privacyMode ? '•••' : week52High?.toFixed(2)}</span>
                      </td>
                      <td className="py-2 px-4 text-center hidden sm:table-cell">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                          isNearHigh ? 'bg-emerald-100 text-emerald-700' : 
                          isFarFromHigh ? 'bg-red-100 text-red-700' : 
                          'bg-amber-100 text-amber-700'
                        }`}>
                          {percentFromHigh}%
                        </span>
                      </td>
                      <td className="py-2 px-4 text-center hidden sm:table-cell">
                        <span className={`text-xs font-bold ${parseFloat(gainLossPercent) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {parseFloat(gainLossPercent) >= 0 ? '+' : ''}{gainLossPercent}%
                        </span>
                      </td>
                      <td className="py-2 px-4 text-center hidden sm:table-cell">
                        <div className="flex items-center gap-2 justify-center">
                          <div className="w-16 h-2 bg-slate-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-blue-500 rounded-full" 
                              style={{ width: `${Math.min(parseFloat(portfolioWeight) * 2, 100)}%` }}
                            ></div>
                          </div>
                          <span className="text-xs text-slate-600 font-medium w-10">{portfolioWeight}%</span>
                        </div>
                      </td>
                      <td className="py-2 px-4 text-right hidden sm:table-cell">
                        <span className="text-xs text-slate-600 font-medium">
                          {stock.volume ? stock.volume.toLocaleString() : '—'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Mobile Stock Detail Popup */}
        {selectedStockPopup && (
          <div 
            className="sm:hidden fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedStockPopup(null)}
          >
            <div 
              className="bg-white rounded-xl shadow-xl w-full max-w-sm overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Popup Header */}
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-3">
                <div className="flex items-center justify-between">
                  <div>
                    <a 
                      href={`https://www.google.com/finance/quote/${selectedStockPopup.symbol}:${selectedStockPopup.symbol.includes('BINANCE:') ? 'CRYPTO' : 'NASDAQ'}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-bold text-white text-lg hover:underline"
                    >
                      {selectedStockPopup.symbol}
                    </a>
                    <p className="text-white/80 text-xs">{selectedStockPopup.name}</p>
                  </div>
                  <button 
                    onClick={() => setSelectedStockPopup(null)}
                    className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center text-white"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              
              {/* Popup Content */}
              <div className="p-4 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-[10px] text-slate-500 uppercase font-semibold">Current Price</p>
                    <p className="text-lg font-bold text-slate-900">${privacyMode ? '•••' : selectedStockPopup.currentPrice?.toFixed(2)}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-[10px] text-slate-500 uppercase font-semibold">Cost Basis</p>
                    <p className="text-lg font-bold text-slate-900">${privacyMode ? '•••' : selectedStockPopup.costBasis?.toFixed(2)}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-[10px] text-slate-500 uppercase font-semibold">52W High</p>
                    <p className="text-lg font-bold text-slate-900">${privacyMode ? '•••' : selectedStockPopup.week52High?.toFixed(2)}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-[10px] text-slate-500 uppercase font-semibold">% From High</p>
                    <p className={`text-lg font-bold ${parseFloat(selectedStockPopup.percentFromHigh) >= -5 ? 'text-emerald-600' : parseFloat(selectedStockPopup.percentFromHigh) <= -20 ? 'text-red-600' : 'text-amber-600'}`}>
                      {selectedStockPopup.percentFromHigh}%
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-[10px] text-slate-500 uppercase font-semibold">Gain/Loss %</p>
                    <p className={`text-lg font-bold ${parseFloat(selectedStockPopup.gainLossPercent) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {parseFloat(selectedStockPopup.gainLossPercent) >= 0 ? '+' : ''}{selectedStockPopup.gainLossPercent}%
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-[10px] text-slate-500 uppercase font-semibold">Portfolio Weight</p>
                    <p className="text-lg font-bold text-blue-600">{selectedStockPopup.portfolioWeight}%</p>
                  </div>
                </div>
                
                {/* Additional Info */}
                <div className="border-t pt-3 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Shares</span>
                    <span className="font-medium text-slate-900">{selectedStockPopup.shares?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Total Value</span>
                    <span className="font-medium text-slate-900">${privacyMode ? '•••••' : selectedStockPopup.totalValue?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Total Cost</span>
                    <span className="font-medium text-slate-900">${privacyMode ? '•••••' : selectedStockPopup.costTotal?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Gain/Loss</span>
                    <span className={`font-bold ${selectedStockPopup.gainLoss >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {selectedStockPopup.gainLoss >= 0 ? '+' : ''}${privacyMode ? '•••••' : selectedStockPopup.gainLoss?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Platform</span>
                    <span className="font-medium text-slate-700 bg-slate-100 px-2 py-0.5 rounded text-xs">{selectedStockPopup.platform || 'Unknown'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Volume</span>
                    <span className="font-medium text-slate-900">{selectedStockPopup.volume ? selectedStockPopup.volume.toLocaleString() : '—'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StockAnalyticsPanel;
